import React from "react";
import { 
  Package, DollarSign, Tag, FileText, 
  Clock, CheckCircle, Boxes, ShieldAlert,
  Hash, Maximize, FileCode, Layers, Info, IndianRupee, MapPin, Receipt, Activity, List, X
} from "lucide-react";
import { Modal, Badge } from "../../../components/ui";
import { fmt } from "../../../lib/formatters";

// Helper component for uniform info display without icons
const Field = ({ label, value }) => (
  <div className="flex flex-col">
    <span className="text-[12px] font-semibold text-slate-700 mb-0.5">{label}</span>
    <span className="text-[13px] font-medium text-slate-500">{value || '—'}</span>
  </div>
);

export const ItemDetailsModal = ({ item, onClose }) => {
  const [isImageOpen, setIsImageOpen] = React.useState(false);

  if (!item) return null;
  
  // Resolve nested properties if they exist
  const meta = item.item || {};
  const name = meta.itemName || meta.name || item.itemName || item.name || 'Unknown Item';
  const itemCode = meta.itemCode || item.itemCode || '—';
  const sku = meta.sku || item.sku || '—';
  
  // Helper to safely extract string from possible populated object
  const resolveValue = (obj, fields = ['title', 'name', 'unitName', 'label', 'categoryName', 'vendorName', 'brandName']) => {
    if (!obj) return null;
    if (typeof obj === 'string') return obj;
    if (typeof obj === 'object') {
      for (const f of fields) {
        if (obj[f] && typeof obj[f] === 'string') return obj[f];
      }
    }
    return null;
  };

  let unit = resolveValue(meta.units) || resolveValue(meta.unit) || resolveValue(item.units) || resolveValue(item.unit) || item.uom || 'NOS';
  if (String(unit).trim() === '1') unit = 'NOS';
  const vendorName = resolveValue(meta.vendor) || resolveValue(item.vendor) || resolveValue(item.vendorName) || resolveValue(meta.supplier) || resolveValue(item.supplier) || '—';
  
  // Robust quantity resolution
  const availableQty = Number(item.availableQty ?? item.stock ?? item.quantity ?? 0);
  const minQty = Number(meta.minQty || item.minQty || 0);
  
  // Financials
  const blum = Number(item.blumPrice) || 0;
  const verve = Number(item.vervePrice) || 0;
  const priceToUse = blum > 0 ? blum : verve;
  const totalPurchased = Number(item.quantity ?? item.initialQty ?? item.availableQty ?? item.stock ?? item.moq ?? 0);
  const totalCost = priceToUse * totalPurchased;
  
  const categoryName = resolveValue(meta.category) || resolveValue(item.category) || '—';
  
  const description = meta.description || item.description || meta.itemDescription || item.itemDescription || meta.notes || item.notes || meta.details || item.details;
  const statusKey = availableQty > minQty ? 'in_stock' : (availableQty > 0 ? 'low_stock' : 'out_of_stock');
  
  const headerTitle = (
    <div>
      <div className="text-xl font-bold tracking-widest text-white">VERVE KITCHENS</div>
      <div className="text-[10px] uppercase tracking-widest text-white/70 mt-1">ITEM DETAILS</div>
    </div>
  );

  return (
    <Modal isOpen={!!item} onClose={onClose} title={headerTitle} size="4xl" className="font-sans">
      <div className="flex flex-col md:flex-row gap-8">
        
        {/* Left Pane - Information Details */}
        <div className="flex-1 space-y-6">
          <div>
            <h4 className="text-[14px] font-semibold text-slate-700 mb-4 border-b-2 border-[#f9a825] pb-2 inline-block">
              Information Details
            </h4>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-6 gap-x-4">
              <Field label="Vendor Name" value={vendorName} />
              <Field label="Category" value={categoryName} />
              <Field label="Sub Category" value={item.subCategory || '—'} />
              
              <Field label="Article ID" value={item.articleId || '—'} />
              <Field label="Internal Type" value={item.internalType || '—'} />
              <Field label="Shutter Type" value={item.shutterType || '—'} />
              
              <Field label="Width" value={item.width || '—'} />
              <Field label="Height" value={item.height || '—'} />
              <Field label="Depth" value={item.depth || '—'} />
              
              <Field label="UOM" value={item.uom || unit} />
              <Field label="Image" value={
                item.image && item.image !== '-' ? (
                  item.image === 'HAS_IMAGE' ? 'Available' : (
                    <img 
                      src={item.image} 
                      alt="Thumbnail" 
                      onClick={() => setIsImageOpen(true)}
                      className="w-10 h-10 object-contain rounded border border-slate-200 cursor-pointer hover:shadow-md transition-all mt-1 bg-white" 
                    />
                  )
                ) : '—'
              } />
              <Field label="Date" value={item.date || item.invoiceDate ? new Date(item.date || item.invoiceDate).toLocaleDateString() : '—'} />
            </div>
          </div>
          
          <div>
             <h4 className="text-[14px] font-semibold text-slate-700 mb-4 border-b-2 border-[#f9a825] pb-2 inline-block mt-4">
              Description & Additional Info
            </h4>
            <div className="grid grid-cols-1 gap-6">
               <Field label="Description" value={description} />
               <Field label="Information" value={item.information} />
               <Field label="Remarks" value={item.remark || item.remarks} />
            </div>
          </div>
        </div>

        {/* Right Pane - Financials & Stock */}
        <div className="w-full md:w-[320px] shrink-0 space-y-6">
          
          {/* Stock Status */}
          <div className="bg-slate-50/50 rounded-lg border-t-4 border-t-slate-700 p-5 shadow-sm">
            <h4 className="text-[14px] font-semibold text-slate-700 mb-4">
              Stock & Availability
            </h4>
            
            <div className="flex items-end gap-2 mb-4">
              <span className={`text-4xl font-semibold tracking-tighter leading-none ${statusKey === 'in_stock' ? 'text-slate-700' : 'text-rose-600'}`}>
                {availableQty}
              </span>
              <span className="text-sm text-slate-500 font-semibold mb-1">{unit}</span>
            </div>
            
            <div className="space-y-3 pt-4 border-t border-slate-200/60">
              <div className="flex justify-between items-center">
                <span className="text-[12px] font-semibold text-slate-700">Status</span>
                <Badge status={statusKey} />
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[12px] font-semibold text-slate-700">MOQ</span>
                <span className="text-[13px] font-medium text-slate-500">{item.moq !== undefined ? item.moq : '—'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[12px] font-semibold text-slate-700">Version</span>
                <span className="text-[13px] font-medium text-slate-500">{item.version || '—'}</span>
              </div>
            </div>
          </div>

          {/* Financials */}
          <div className="bg-slate-50/50 rounded-lg border-t-4 border-t-[#f9a825] p-5 shadow-sm">
            <h4 className="text-[14px] font-semibold text-slate-700 mb-4">
              Financials
            </h4>
            
            <div className="flex flex-col mb-4">
              <span className="text-[12px] font-semibold text-slate-700 mb-1">Total Purchases</span>
              <span className="text-xl font-medium text-slate-900 tracking-tight leading-none">
                {fmt(totalCost)}
              </span>
            </div>
            
            <div className="space-y-3 pt-4 border-t border-slate-200/60">
              <div className="flex justify-between items-center">
                <span className="text-[12px] font-semibold text-slate-700">Blum Price</span>
                <span className="text-[13px] font-medium text-slate-500">{item.blumPrice ? fmt(item.blumPrice) : '—'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[12px] font-semibold text-slate-700">Verve Price</span>
                <span className="text-[13px] font-medium text-slate-500">{item.vervePrice ? fmt(item.vervePrice) : '—'}</span>
              </div>
               {totalPurchased > 0 && (
                  <div className="flex justify-between items-center pt-2 border-t border-slate-200/60 mt-1">
                    <span className="text-[12px] font-semibold text-slate-700">Total Purchased / Quantity</span>
                    <span className="text-[13px] font-medium text-slate-500">{totalPurchased} {unit}</span>
                  </div>
               )}
            </div>
          </div>
          
        </div>
      </div>
      
      {/* Full Size Image Overlay */}
      {isImageOpen && item.image && item.image !== 'HAS_IMAGE' && (
        <div 
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-sm animate-in fade-in duration-200" 
          onClick={() => setIsImageOpen(false)}
        >
          <button className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors">
            <X size={32} />
          </button>
          <img 
            src={item.image} 
            alt="Enlarged" 
            className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl" 
            onClick={(e) => e.stopPropagation()} 
          />
        </div>
      )}
    </Modal>
  );
};

